import { createContext, useCallback, useEffect, useState } from 'react';

/**
 * Breadcrumb trail for the Book → Chapter → Passage → Sentence → Word
 * vertical navigation axis. Trail is ephemeral: persisted in sessionStorage
 * so a hard refresh keeps it, but nothing lives on the server.
 *
 * Each crumb: { level, label, href }
 *   level ∈ LEVEL_ORDER — drives ordering + drop-behind-on-push semantics.
 *   label — rendered text. Strings are truncated; JSX is rendered as-is.
 *   href  — optional; if present, the crumb is a link.
 */
export const LEVEL_ORDER = ['book', 'chapter', 'passage', 'sentence', 'word'];

export const BreadcrumbContext = createContext({
  crumbs: [],
  push: () => {},
  replace: () => {},
  clear: () => {},
});

const STORAGE_KEY = 'studyfit:breadcrumbs';

function readStored() {
  if (typeof window === 'undefined') return [];
  try {
    const raw = sessionStorage.getItem(STORAGE_KEY);
    return raw ? JSON.parse(raw) : [];
  } catch {
    return [];
  }
}

export function BreadcrumbProvider({ children }) {
  const [crumbs, setCrumbs] = useState(readStored);

  useEffect(() => {
    try {
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(crumbs));
    } catch {
      // storage full or disabled — fine to no-op
    }
  }, [crumbs]);

  // Push a crumb at its level: drop any crumbs at this level or deeper, then append.
  // If a crumb with the same level+href already exists, this is a no-op
  // (avoids churning the trail when a page re-mounts on the same URL).
  const push = useCallback((crumb) => {
    const idx = LEVEL_ORDER.indexOf(crumb.level);
    if (idx < 0) return;
    setCrumbs((prev) => {
      const existing = prev.find((c) => c.level === crumb.level && c.href === crumb.href);
      if (existing) {
        const kept = prev.filter((c) => LEVEL_ORDER.indexOf(c.level) < idx);
        return [...kept, { ...existing, ...crumb }];
      }
      const kept = prev.filter((c) => LEVEL_ORDER.indexOf(c.level) < idx);
      return [...kept, crumb];
    });
  }, []);

  // Replace the whole trail atomically. Used when a page knows its full context
  // up front (e.g. Sentence Studio Show reconstructing book+chapter+sentence).
  const replace = useCallback((nextCrumbs) => {
    setCrumbs(Array.isArray(nextCrumbs) ? nextCrumbs.slice() : []);
  }, []);

  const clear = useCallback(() => {
    setCrumbs([]);
    try { sessionStorage.removeItem(STORAGE_KEY); } catch {}
  }, []);

  return (
    <BreadcrumbContext.Provider value={{ crumbs, push, replace, clear }}>
      {children}
    </BreadcrumbContext.Provider>
  );
}
